import { useMemo } from 'react';

import { useActiveChannel } from '~/context/PartnerConfigContext';
import { MarketplacePartner } from '~/types/marketplace';
import { parseBundleSubcats } from '~/utils/premarketplace/bundle-subcategories';
import { computeVisibleSteps, parseStepFlow } from '~/utils/premarketplace/step-flow';
import type { VisibleWhen } from '~/utils/premarketplace/visible-when';

export type StepIds =
  | 'introduction'
  | 'eligibility-check'
  | 'location'
  | 'categories'
  | 'household'
  | 'upgrade-profile'
  | 'financing-interest'
  | 'bundle-categories'
  | 'intent'
  | 'topics'
  | 'education'
  | 'purchase-priority'
  | 'connect-with-vendors'
  | 'contact-time'
  | 'recommended-vendors'
  | 'bill-upload'
  | 'contact';

export interface StepConfig {
  id: StepIds;
  required: boolean;
  // Answer-driven branching (INT-1294). Absent = shown per `required` (today's behaviour).
  visibleWhen?: VisibleWhen;
}

export interface ModalStepConfiguration {
  partner: MarketplacePartner;
  steps: StepConfig[];
}

// Channel-derived inputs to branching: the parsed base flow and the bundle-eligible
// category slugs. Shared by StepProvider (which computes the answer-reactive flow)
// and usePartnerStepConfiguration (the static entry flow). No answers dependency,
// so callers don't need PremarketplaceProvider.
export const usePremarketplaceBaseFlow = (): { baseFlow: StepConfig[]; bundleSlugs?: ReadonlySet<string> } => {
  const activeChannel = useActiveChannel();
  const stepConfiguration = activeChannel?.preMarketplace?.stepConfiguration;

  const baseFlow = useMemo(
    () => parseStepFlow(activeChannel?.preMarketplace?.stepFlow),
    [activeChannel?.preMarketplace?.stepFlow],
  );

  // Only single-select category flows offer bundling (matches prior behaviour,
  // where the bundle step was wired to the single-select radio only).
  const bundleSlugs = useMemo(() => {
    const bundle = stepConfiguration?.bundleCategories;
    const isMultiSelect = stepConfiguration?.categories?.multiSelection ?? true;
    if (!bundle?.enabled || isMultiSelect) return undefined;
    return new Set(Object.keys(parseBundleSubcats(bundle.subcategories)));
  }, [stepConfiguration?.bundleCategories, stepConfiguration?.categories?.multiSelection]);

  return { baseFlow, bundleSlugs };
};

const EMPTY_ANSWERS: ReadonlyMap<StepIds, Set<string>> = new Map();

// The entry flow with no answers yet — used by the index route to pick the first
// step. With empty answers this equals today's required-filtered flow.
export const usePartnerStepConfiguration = (partner?: MarketplacePartner): ModalStepConfiguration | null => {
  const { baseFlow, bundleSlugs } = usePremarketplaceBaseFlow();
  const steps = useMemo(() => computeVisibleSteps(baseFlow, EMPTY_ANSWERS, bundleSlugs), [baseFlow, bundleSlugs]);

  return useMemo(() => (partner ? { partner, steps } : null), [partner, steps]);
};
